import React from "react";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { Box, Link, Tab, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  checkUserDetailsFromCookie,
  checkUserPermissions,
  fetchRoles,
} from "../../api_calls/userManagement";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import { StyledMainBox, StyledTabs } from "../../components/commonComponents/StyledComponents/styled";
import ListingDataTable from "../../components/commonComponents/TabularLists/ListingDataTable";
import {
  ListingCellCenter,
  ListingCellLeft,
  ListingHeaderContent,
} from "../../components/commonComponents/TabularLists/styles";
import { deleteItems } from "../../Store/actions";
import RoleDetail from "./modalContent/RoleDetail";

function a11yProps(index) {
  return {
    id: `roles-tab-${index}`,
    "aria-controls": `roles-tabpanel-${index}`,
  };
}

const INTRO_TARGET_BY_TAB = {
  retailer: "",
  tenant: "tenant ",
  brand: "brand ",
  cyreen: "Cyreen ",
};

const getRolesIntroCopy = (tabUserType) => {
  const target = INTRO_TARGET_BY_TAB[tabUserType] ?? "";
  return (
    `This section lists all permission roles that you are able to assign to ${target}users. ` +
    "You may review the details of each role by clicking on the individual roles."
  );
};

const isBrandRoleName = (role) =>
  role.role_name != null && String(role.role_name).toLowerCase() === "brand";

const filterRolesForTab = (roles, tabUserType, permissionScope) => {
  const list = Array.isArray(roles) ? roles : [];

  if (tabUserType === "brand") {
    return list.filter(
      (role) => isBrandRoleName(role) === true && permissionScope.user_list_can_edit_brand === true
    );
  }

  return list.filter((role) => {
    if (isBrandRoleName(role) === true || role.deactivated === true) {
      return false;
    }
    if (tabUserType === "cyreen") {
      return (
        permissionScope.user_list_can_edit_cyreen === true &&
        permissionScope.user_list_can_delete_cyreen === true
      );
    }
    if (tabUserType === "retailer") {
      return permissionScope.user_list_can_edit_external === true;
    }
    return true;
  });
};

const Roles = () => {
  const [selectedRoleInfo, setSelectedRoleInfo] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(0);
  // One saved list per tab. A tab that is already here is not requested again.
  const [rolesByType, setRolesByType] = React.useState({});

  const checkUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const userType = checkUserDetails.user_detail.user_type;
  const deactivatedSections =
    checkUserDetails.success === true ? checkUserDetails.user_detail.deactivated_sections : [];
  const showBrandsSection = userType === "cyreen" || !deactivatedSections.includes("campaign_list");

  const dispatchAction = useDispatch();

  let tabsConfig = [
    {
      label: "USERS",
      unique_key: "rolesRetailerTab",
      user_type: "retailer",
    },
  ];

  // Tenant roles tab follows the tenant edit flag. The Cyreen tab below still uses user_list_can_edit_cyreen.
  if (["tenant", "cyreen"].includes(userType) && permissionScope.user_list_can_edit_tenant === true) {
    tabsConfig.push({
      label: "TENANT",
      unique_key: "rolesTenantTab",
      user_type: "tenant",
    });
  }

  if (
    permissionScope.user_list_can_edit_brand === true &&
    permissionScope.user_list_can_delete_brand === true &&
    showBrandsSection === true
  ) {
    tabsConfig.push({
      label: "BRANDS",
      unique_key: "rolesBrandTab",
      user_type: "brand",
    });
  }

  if (
    userType === "cyreen" &&
    permissionScope.user_list_can_edit_cyreen === true &&
    permissionScope.user_list_can_delete_cyreen === true
  ) {
    tabsConfig.push({
      label: "CYREEN",
      unique_key: "rolesCyreenTab",
      user_type: "cyreen",
    });
  }

  const showTabsView = tabsConfig.length > 1;
  const activeTab = tabsConfig[value] ?? tabsConfig[0];
  const activeUserType = activeTab?.user_type ?? "retailer";

  const closeSection = (newOpen) => {
    setOpen(newOpen);
    dispatchAction(deleteItems(["assignable_users_list", "assignable_cyreen_users"]));
  };

  const [sortingMeasure, setSortingMeasure] = React.useState({
    rolename: "asc",
    userCount: "asc",
  });
  const [sortingParamName, setSortingParamName] = React.useState("rolename");

  const sortedResults = (toBeSortedList) => {
    return toBeSortedList.slice().sort((a, b) => {
      if (sortingParamName === "rolename") {
        return sortingMeasure.rolename === "asc"
          ? a.role_name.localeCompare(b.role_name)
          : b.role_name.localeCompare(a.role_name);
      }
      if (sortingParamName === "userCount") {
        return sortingMeasure.userCount === "asc"
          ? parseInt(a.assigned_users_count, 10) - parseInt(b.assigned_users_count, 10)
          : parseInt(b.assigned_users_count, 10) - parseInt(a.assigned_users_count, 10);
      }
      return sortingMeasure.rolename === "asc"
        ? a.role_name.localeCompare(b.role_name)
        : b.role_name.localeCompare(a.role_name);
    });
  };

  const roleList = Array.isArray(rolesByType[activeUserType]) ? rolesByType[activeUserType] : [];

  const rows = React.useMemo(() => {
    const filtered = filterRolesForTab(roleList, activeUserType, permissionScope);
    return sortedResults(filtered);
  }, [roleList, activeUserType, permissionScope, sortingMeasure, sortingParamName]);

  const handleSortingParamChange = (paramName) => {
    setSortingMeasure((prevState) => ({
      ...prevState,
      [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
    }));
    setSortingParamName(paramName);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const columns = React.useMemo(
    () => [
      {
        id: "name",
        header: (
          <ListingHeaderContent>
            Name
            {sortingMeasure.rolename === "asc" ? (
              <ArrowDropDown sx={{ cursor: "pointer" }} onClick={() => handleSortingParamChange("rolename")} />
            ) : (
              <ArrowDropUp sx={{ cursor: "pointer" }} onClick={() => handleSortingParamChange("rolename")} />
            )}
          </ListingHeaderContent>
        ),
        headerSx: { width: 220, minWidth: 220 },
        cellSx: { width: 220, minWidth: 220 },
        render: (row) => (
          <ListingCellLeft>
            <Link
              href="#"
              underline="none"
              aria-label={"check details for " + row.role_name}
              title={row.role_name}
              sx={{ color: "#AF3241" }}
              onClick={(e) => {
                e.preventDefault();
                setSelectedRoleInfo(row);
                setOpen(true);
              }}
            >
              {row.role_name}
            </Link>
          </ListingCellLeft>
        ),
      },
      {
        id: "description",
        header: "Description",
        render: (row) => <ListingCellLeft>{row.description}</ListingCellLeft>,
      },
      {
        id: "modules",
        header: "Modules",
        render: (row) => <ListingCellLeft>{row.modules}</ListingCellLeft>,
      },
      {
        id: "users",
        header: (
          <ListingHeaderContent>
            Users
            {sortingMeasure.userCount === "asc" ? (
              <ArrowDropDown sx={{ cursor: "pointer" }} onClick={() => handleSortingParamChange("userCount")} />
            ) : (
              <ArrowDropUp sx={{ cursor: "pointer" }} onClick={() => handleSortingParamChange("userCount")} />
            )}
          </ListingHeaderContent>
        ),
        headerAlign: "center",
        align: "center",
        headerSx: { width: 100, minWidth: 100 },
        cellSx: { width: 100, minWidth: 100 },
        render: (row) => (
          <ListingCellCenter>{row.assigned_users_count}</ListingCellCenter>
        ),
      },
    ],
    [sortingMeasure]
  );

  // First visit to a tab loads that user type. Coming back uses the saved list.
  React.useEffect(() => {
    if (Array.isArray(rolesByType[activeUserType])) {
      return undefined;
    }

    let shouldSaveResult = true;

    fetchRoles(true, { type: activeUserType }).then((result) => {
      if (shouldSaveResult !== true) {
        return;
      }
      const roles = result.success === true && Array.isArray(result.roles_list) ? result.roles_list : [];
      setRolesByType((previousLists) => {
        return {
          ...previousLists,
          [activeUserType]: roles,
        };
      });
    });

    return () => {
      shouldSaveResult = false;
    };
  }, [activeUserType, rolesByType]);

  const tableLoading = typeof rolesByType[activeUserType] === "undefined";

  return (
    <>
      <StyledMainBox component="main">
        <Box height={30} />
        <h1>Roles</h1>

        {showTabsView === true ? (
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: "0",
              zIndex: 1,
              backgroundColor: "#fff",
            }}
          >
            <Box component="div">
              <StyledTabs value={value} onChange={handleChange} aria-label="roles-by-user-type" textColor={"#F04B55"}>
                {tabsConfig.map((tabDetail, index) => (
                  <Tab
                    key={tabDetail.unique_key}
                    aria-label={"tab for " + tabDetail.label}
                    label={tabDetail.label}
                    {...a11yProps(index)}
                  />
                ))}
              </StyledTabs>
            </Box>
          </Box>
        ) : null}

        <Box height={showTabsView === true ? 5 : 10} />
        <Typography variant="body2" sx={{ fontSize: "15px" }}>
          {getRolesIntroCopy(activeUserType)}
        </Typography>
        <Box height={30} />
        <ListingDataTable
          columns={columns}
          rows={rows}
          rowKey={(row, idx) => row.id ?? row.role_name ?? idx}
          emptyText="No roles found"
          pagination={null}
          onPageChange={null}
          onPageSizeChange={null}
          loading={tableLoading}
        />
      </StyledMainBox>
      {open === true && <RoleDetail showSection={open} closeSection={closeSection} roleInfo={selectedRoleInfo} />}
    </>
  );
};

export default AccessControl(Roles, ["user_list_can_view"], [], ["user_section", "role_list"]);
